import {
  Authenticated,
  Unauthenticated,
  useQuery,
  useMutation,
} from "convex/react";
import { api } from "../convex/_generated/api";
import { Id } from "../convex/_generated/dataModel";
import { SignInForm } from "./SignInForm";
import { SignOutButton } from "./SignOutButton";
import { Toaster, toast } from "sonner";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { ThemeProvider } from "./lib/theme";
import { ThemeToggle } from "./components/ThemeToggle";
import {
  OrganizationProvider,
  useOrganization,
} from "./lib/organization-context";
import { useAuthActions } from "@convex-dev/auth/react";
import { ProfileAvatar } from "./components/ProfileAvatar";
import { InterruptWatcher } from "./components/InterruptWatcher";
import { PictureInPictureTimer } from "./components/PictureInPictureTimer";
import { useTimerContext } from "./lib/timer-context";
import type { PiPTimerState } from "./lib/pip";
import {
  ensurePushSubscription,
  isPushSupported,
  setupPushMessageListener,
} from "./lib/push";
import { clearAppBadge } from "./lib/badgeApi";
import {
  updateTimerTitle,
  clearTimerTitle,
  setPageVisibility,
} from "./lib/attention";
import { buildAppPath, stripBasePath } from "./lib/basePath";
import {
  type MarketingPageSlug,
  marketingPageToPath,
} from "./lib/marketingPages";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { FeedbackModal } from "./components/FeedbackModal";

// Define AppPage type locally (matches ProfilePage export)
export type AppPage =
  | "timer"
  | "clients"
  | "projects"
  | "categories"
  | "settings"
  | "profile"
  | "testEmail"
  | "retainerTesting"
  | "systemDashboard"
  | "feedbackAdmin";

// Settings tab type (matches Settings component)
type SettingsTab =
  | "timer"
  | "notifications"
  | "budget"
  | "team"
  | "apiKeys"
  | "account"
  | "feedback";

// Lazy-loaded components for code splitting
const ModernDashboard = lazy(() =>
  import("./components/ModernDashboard").then((m) => ({
    default: m.ModernDashboard,
  })),
);
const Settings = lazy(() =>
  import("./components/Settings").then((m) => ({ default: m.Settings })),
);
const ClientsPage = lazy(() =>
  import("./components/ClientsPage").then((m) => ({ default: m.ClientsPage })),
);
const ProjectsPage = lazy(() =>
  import("./components/ProjectsPage").then((m) => ({
    default: m.ProjectsPage,
  })),
);
const ProjectDetailPage = lazy(() =>
  import("./components/ProjectDetailPage").then((m) => ({
    default: m.ProjectDetailPage,
  })),
);
const ProfilePage = lazy(() =>
  import("./components/ProfilePage").then((m) => ({ default: m.ProfilePage })),
);
const InvitePage = lazy(() =>
  import("./components/InvitePage").then((m) => ({ default: m.InvitePage })),
);
const TestEmailPage = lazy(() =>
  import("./components/TestEmailPage").then((m) => ({
    default: m.TestEmailPage,
  })),
);
const SystemDashboardPage = lazy(() =>
  import("./components/SystemDashboardPage").then((m) => ({
    default: m.SystemDashboardPage,
  })),
);
const PaywallModal = lazy(() =>
  import("./components/PaywallModal").then((m) => ({
    default: m.PaywallModal,
  })),
);
const FeedbackAdmin = lazy(() =>
  import("./components/FeedbackAdmin").then((m) => ({
    default: m.FeedbackAdmin,
  })),
);

// Marketing pages - lazy loaded
const AboutPage = lazy(() =>
  import("./components/MarketingPages").then((m) => ({ default: m.AboutPage })),
);
const FeaturesPage = lazy(() =>
  import("./components/MarketingPages").then((m) => ({
    default: m.FeaturesPage,
  })),
);
const FaqPage = lazy(() =>
  import("./components/MarketingPages").then((m) => ({ default: m.FaqPage })),
);
const PricingPage = lazy(() =>
  import("./components/MarketingPages").then((m) => ({
    default: m.PricingPage,
  })),
);
const PrivacyPolicyPage = lazy(() =>
  import("./components/MarketingPages").then((m) => ({
    default: m.PrivacyPolicyPage,
  })),
);
const SupportPage = lazy(() =>
  import("./components/MarketingPages").then((m) => ({
    default: m.SupportPage,
  })),
);
const TermsPage = lazy(() =>
  import("./components/MarketingPages").then((m) => ({ default: m.TermsPage })),
);

// Mobile/Desktop auth pages - lazy loaded
const MobileLandingPage = lazy(() =>
  import("./components/MobileLandingPage").then((m) => ({
    default: m.MobileLandingPage,
  })),
);
const MobileSignIn = lazy(() =>
  import("./components/MobileSignIn").then((m) => ({
    default: m.MobileSignIn,
  })),
);
const MobileSignUp = lazy(() =>
  import("./components/MobileSignUp").then((m) => ({
    default: m.MobileSignUp,
  })),
);
const DesktopLandingPage = lazy(() =>
  import("./components/DesktopLandingPage").then((m) => ({
    default: m.DesktopLandingPage,
  })),
);
const DesktopSignIn = lazy(() =>
  import("./components/DesktopSignIn").then((m) => ({
    default: m.DesktopSignIn,
  })),
);
const DesktopSignUp = lazy(() =>
  import("./components/DesktopSignUp").then((m) => ({
    default: m.DesktopSignUp,
  })),
);

// Loading spinner component for Suspense fallback
function PageLoader() {
  return (
    <div className="flex justify-center items-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

// Mobile detection utility
function isMobileDevice(): boolean {
  return (
    window.innerWidth < 768 ||
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  );
}

// Mobile auth page type
type MobileAuthPage = "landing" | "sign-in" | "sign-up";

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function AppContent() {
  const [inviteToken, clearInviteToken] = useInviteToken();

  // Check if there are deep link parameters that should be preserved through auth
  const hasDeepLinkParams = () => {
    const params = new URLSearchParams(window.location.search);
    return params.has("project") || params.has("task");
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-dark-gradient">
      {inviteToken ? (
        <InviteRoute token={inviteToken} onComplete={clearInviteToken} />
      ) : (
        <>
          <Authenticated>
            <AuthenticatedAppWrapper />
          </Authenticated>
          <Unauthenticated>
            <UnauthenticatedViewWrapper />
          </Unauthenticated>
        </>
      )}
      <Toaster />
      <PWAInstallPrompt />
    </div>
  );
}

function AuthenticatedAppWrapper() {
  return <AuthenticatedApp />;
}

function UnauthenticatedViewWrapper() {
  return <UnauthenticatedView />;
}

function AuthenticatedApp() {
  const [currentPage, setCurrentPage] = useState<AppPage>("timer");
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [pushSwitchRequest, setPushSwitchRequest] = useState<any | null>(null);
  const [currentWorkspace, setCurrentWorkspace] = useState<"personal" | "work">(
    "work",
  );
  const [clientFilter, setClientFilter] = useState<string | null>(null);
  const [workspaceInitialized, setWorkspaceInitialized] = useState(false);
  const [settingsTab, setSettingsTab] = useState<SettingsTab | undefined>(
    undefined,
  );
  const [showGlobalPaywall, setShowGlobalPaywall] = useState(false);
  const [paywallMode, setPaywallMode] = useState<"limit" | "upgrade">("limit");
  const [projectDetailTab, setProjectDetailTab] = useState<"entries" | "tasks">(
    "tasks",
  );
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const loggedInUser = useQuery(api.auth.loggedInUser);
  const { signOut } = useAuthActions();
  const stopTimerMutation = useMutation(api.timer.stop);
  const startTimerMutation = useMutation(api.timer.start);
  const ackInterrupt = useMutation(api.timer.ackInterrupt);
  const savePushSubscription = useMutation(
    api.pushNotifications.savePushSubscription,
  );
  const ensureWorkspace = useMutation(
    api.organizations.ensurePersonalWorkspace,
  );
  const pushListenerCleanup = useRef<(() => void) | null>(null);
  const hasAnnouncedAuth = useRef<boolean>(false);
  const workspaceInitializedRef = useRef<boolean>(false);

  // Global timer context for PiP synchronization
  const { now } = useTimerContext();

  // Query running timer and related data for PiP (stays active across all pages)
  const runningTimer = useQuery(api.timer.getRunningTimer, {
    workspaceType: currentWorkspace,
  });
  const userSettings = useQuery(api.users.getUserSettings);
  const currentProject = useQuery(
    api.projects.get,
    runningTimer?.projectId ? { id: runningTimer.projectId } : "skip",
  );

  // Build PiP timer state (persists across navigation)
  const pipTimerState: PiPTimerState = useMemo(() => {
    if (!runningTimer || !runningTimer.project) {
      return {
        elapsedMs: 0,
        projectName: "",
        isRunning: false,
        currentTime: now,
      };
    }

    const isBreakTimer = runningTimer.isBreakTimer || false;
    const totalElapsedMs = Math.max(0, now - (runningTimer.startedAt || now));
    const breakTimeRemaining =
      isBreakTimer && runningTimer.breakEndsAt
        ? Math.max(0, runningTimer.breakEndsAt - now)
        : 0;

    return {
      elapsedMs: totalElapsedMs,
      projectName: runningTimer.project.name,
      clientName: runningTimer.project.client?.name,
      isRunning: true,
      isInterrupt: runningTimer.awaitingInterruptAck,
      interruptSecondsLeft:
        runningTimer.awaitingInterruptAck &&
        runningTimer.interruptShownAt &&
        userSettings?.gracePeriod
          ? Math.max(
              0,
              Math.ceil(
                (userSettings.gracePeriod * 1000 -
                  (now - runningTimer.interruptShownAt)) /
                  1000,
              ),
            )
          : undefined,
      isBudgetWarning: false,
      isBudgetOverrun: false,
      projectColor: currentProject?.client?.color || "#f97316",
      isBreakTimer,
      breakTimeRemaining: isBreakTimer ? breakTimeRemaining : 0,
      currentTime: now,
    };
  }, [runningTimer, now, userSettings]);

  // Page visibility tracking for title updates (global, stays active across all pages)
  useEffect(() => {
    const handleVisibilityChange = () => {
      setPageVisibility(document.visibilityState === "visible");
    };

    // Set initial state
    setPageVisibility(document.visibilityState === "visible");

    // Listen for visibility changes
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearTimerTitle();
    };
  }, []);

  // Update tab title when timer is running (global, stays active across all pages)
  useEffect(() => {
    if (!runningTimer || !currentProject) {
      clearTimerTitle();
      return;
    }

    const updateTitle = () => {
      const elapsed = now - (runningTimer.startedAt || now);
      const isBreakTimer = runningTimer.isBreakTimer || false;
      const breakTimeRemaining =
        isBreakTimer && runningTimer.breakEndsAt
          ? Math.max(0, runningTimer.breakEndsAt - now)
          : 0;

      // Check if interrupt is active
      if (
        runningTimer.awaitingInterruptAck &&
        runningTimer.interruptShownAt &&
        userSettings?.gracePeriod
      ) {
        const gracePeriodMs = userSettings.gracePeriod * 1000;
        const timeSinceInterrupt = now - runningTimer.interruptShownAt;
        const remainingMs = gracePeriodMs - timeSinceInterrupt;
        const remainingSeconds = Math.max(0, Math.ceil(remainingMs / 1000));

        updateTimerTitle({
          elapsedMs: elapsed,
          projectName: currentProject.name,
          isInterrupt: true,
          interruptSecondsLeft: remainingSeconds,
        });
      } else if (isBreakTimer) {
        // Break timer display
        updateTimerTitle({
          elapsedMs: elapsed,
          projectName: currentProject.name,
          isBreak: true,
          breakTimeRemaining,
        });
      } else {
        // Normal timer display
        updateTimerTitle({
          elapsedMs: elapsed,
          projectName: currentProject.name,
        });
      }
    };

    // Update immediately
    updateTitle();

    // Update every second
    const interval = setInterval(updateTitle, 1000);

    return () => clearInterval(interval);
  }, [runningTimer, currentProject, now, userSettings?.gracePeriod]);

  // Navigation handler that supports settings tab
  const handleNavigate = useCallback(
    (page: AppPage, options?: { settingsTab?: SettingsTab }) => {
      setCurrentPage(page);
      if (page === "settings" && options?.settingsTab) {
        setSettingsTab(options.settingsTab);
      } else {
        setSettingsTab(undefined);
      }
    },
    [],
  );

  // Do not auto sign out if query returns null; allow session to establish post sign-in.
  // The <Authenticated>/<Unauthenticated> gates already handle rendering.

  // Ensure workspace exists for authenticated users (especially new OAuth users)
  useEffect(() => {
    if (!loggedInUser || workspaceInitializedRef.current) {
      return;
    }

    // Mark as initialized to prevent duplicate calls
    workspaceInitializedRef.current = true;

    const initializeWorkspace = async () => {
      try {
        await ensureWorkspace();

        // Initialize RevenueCat with user ID for subscription management
        try {
          const { configureRevenueCat } = await import("./lib/revenuecat");
          await configureRevenueCat(loggedInUser._id);
        } catch (rcError) {
          // RevenueCat init failure is non-fatal, log but continue
          console.warn("RevenueCat initialization failed:", rcError);
        }

        setWorkspaceInitialized(true);
      } catch (error) {
        console.error("Failed to initialize workspace:", error);
        // Reset flag so we can retry
        workspaceInitializedRef.current = false;

        toast.error("Workspace initialization failed", {
          description:
            "Please refresh the page. If the problem persists, contact support.",
        });
      }
    };

    void initializeWorkspace();
  }, [loggedInUser, ensureWorkspace]);

  // One-time welcome toast after successful sign-in
  useEffect(() => {
    if (!hasAnnouncedAuth.current && loggedInUser && workspaceInitialized) {
      hasAnnouncedAuth.current = true;
      toast.success("Welcome to iTimedIT", {
        description: `Signed in as ${loggedInUser.email}`,
      });
      // Navigate to timer after successful sign-in
      setCurrentPage("timer");
    }
  }, [loggedInUser, workspaceInitialized]);

  // Handle deep links from URL parameters (e.g., from email notifications)
  useEffect(() => {
    if (!loggedInUser || !workspaceInitialized) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const projectId = params.get("project");
    const taskId = params.get("task");

    if (projectId) {
      // Small delay to ensure organization context is ready
      const timer = setTimeout(() => {
        try {
          // Navigate to the project detail page
          setSelectedProjectId(projectId);
          setCurrentPage("projects");

          // Clear the URL parameters after handling
          window.history.replaceState({}, "", window.location.pathname);

          if (taskId) {
            toast.info("Navigated to project", {
              description: "Scroll to find the mentioned task",
            });
          }
        } catch (error) {
          console.error("Failed to navigate to deep link:", error);
          toast.error("Could not navigate to the requested project");
        }
      }, 500); // 500ms delay to ensure organization is ready

      return () => clearTimeout(timer);
    }
  }, [loggedInUser, workspaceInitialized]);

  useEffect(() => {
    if (!loggedInUser) {
      return () => {};
    }

    if (!isPushSupported()) {
      return () => {};
    }

    let active = true;

    const cleanup = setupPushMessageListener(async (action, data) => {
      if (!active) return;

      try {
        switch (action) {
          case "stop":
            await stopTimerMutation({ sourceOverride: "manual" });
            break;
          case "snooze":
          case "continue":
            await ackInterrupt({ continue: true });
            break;
          case "switch":
            await stopTimerMutation({ sourceOverride: "manual" });
            setCurrentPage("timer");
            setPushSwitchRequest(data || { from: "notification" });
            toast.info("Select a project to switch to", {
              description: "Project picker opened from push notification",
            });
            break;
          default:
            console.log("Unhandled push action", action, data);
        }
      } catch (error) {
        console.error("Failed to handle push action", action, error);
        toast.error("We couldn't complete the requested timer action.");
      }
    });

    pushListenerCleanup.current = cleanup;

    void (async () => {
      try {
        const subscription = await ensurePushSubscription({
          requestPermission: false,
        });
        if (!subscription) {
          return;
        }

        await savePushSubscription({
          endpoint: subscription.endpoint,
          p256dhKey: subscription.keys.p256dh,
          authKey: subscription.keys.auth,
          userAgent: navigator.userAgent,
        });
      } catch (error) {
        console.error("Failed to ensure push subscription", error);
      }
    })();

    return () => {
      active = false;
      cleanup();
      pushListenerCleanup.current = null;
      // Clear badge when component unmounts
      clearAppBadge();
    };
  }, [
    loggedInUser?._id,
    stopTimerMutation,
    ackInterrupt,
    savePushSubscription,
  ]);

  // Show loading while user data or workspace is being set up
  if (loggedInUser === undefined || (loggedInUser && !workspaceInitialized)) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!loggedInUser) {
    return null;
  }

  return (
    <OrganizationProvider userId={loggedInUser._id}>
      {/* Global interrupt watcher - monitors interrupts on all screens */}
      <InterruptWatcher />
      <WorkspaceSyncEffect
        currentWorkspace={currentWorkspace}
        setCurrentWorkspace={setCurrentWorkspace}
      />

      <div className="flex flex-col min-h-screen">
        {/* Desktop Header */}
        <header className="sticky top-0 z-10 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm h-16 flex justify-between items-center border-b border-gray-200 dark:border-gray-700 shadow-sm px-4 sm:px-6">
          <div className="flex items-center gap-3 sm:gap-6 min-w-0 flex-1">
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <img
                src="/icon.png"
                alt="iTimedIT"
                className="h-6 w-6 sm:h-8 sm:w-8"
              />
              <h2 className="text-lg sm:text-xl font-semibold text-[#F85E00]">
                iTimedIT
              </h2>
            </div>
            <div className="hidden sm:flex sm:items-center sm:gap-4 flex-shrink-0">
              <WorkspaceIndicator
                currentWorkspace={currentWorkspace}
                onWorkspaceChange={setCurrentWorkspace}
                onManageWorkspaces={() => setCurrentPage("profile")}
              />
            </div>
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex gap-2 xl:gap-4 min-w-0">
              <NavButton
                icon={<TimerIcon />}
                label="Timer"
                isActive={currentPage === "timer"}
                onClick={() => setCurrentPage("timer")}
              />
              <NavButton
                icon={<DashboardIcon />}
                label="Dashboard"
                isActive={currentPage === "systemDashboard"}
                onClick={() => setCurrentPage("systemDashboard")}
              />
              <NavButton
                icon={<ClientsIcon />}
                label="Clients"
                isActive={currentPage === "clients"}
                onClick={() => setCurrentPage("clients")}
              />
              <NavButton
                icon={<ProjectsIcon />}
                label="Projects"
                isActive={currentPage === "projects"}
                onClick={() => setCurrentPage("projects")}
              />
            </nav>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <PictureInPictureTimer
              timerState={pipTimerState}
              disabled={!runningTimer}
              iconOnly
            />
            <ThemeToggle />
            <button
              onClick={() => setShowFeedbackModal(true)}
              className="p-2 rounded-lg transition-colors bg-primary hover:bg-primary-hover"
              aria-label="Help & Feedback"
              title="Help & Feedback"
            >
              <div className="w-5 h-5 text-white">
                <HelpIcon />
              </div>
            </button>
            <button
              onClick={() => setCurrentPage("settings")}
              className="p-2 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/50"
              aria-label="Settings"
            >
              <div className="w-5 h-5 text-gray-600 dark:text-gray-300">
                <SettingsIcon />
              </div>
            </button>
            <ProfileAvatar
              user={loggedInUser}
              onOpenProfile={() => setCurrentPage("profile")}
              isActive={currentPage === "profile"}
            />
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 pb-20 md:pb-8">
          <div className="max-w-7xl mx-auto">
            <Suspense fallback={<PageLoader />}>
              {currentPage === "timer" && (
                <ModernDashboard
                  pushSwitchRequest={pushSwitchRequest}
                  onPushSwitchHandled={() => setPushSwitchRequest(null)}
                  workspaceType={currentWorkspace}
                  onWorkspaceChange={setCurrentWorkspace}
                  onNavigateToProjectEntries={(projectId) => {
                    setSelectedProjectId(projectId);
                    setProjectDetailTab("entries");
                    setCurrentPage("projects");
                  }}
                  onNavigateToClient={(clientId) => {
                    setSelectedClientId(clientId);
                    setCurrentPage("clients");
                  }}
                  onNavigateToProject={(projectId) => {
                    setSelectedProjectId(projectId);
                    setProjectDetailTab("tasks");
                    setCurrentPage("projects");
                  }}
                  onNavigateToClients={() => setCurrentPage("clients")}
                  onNavigateToProjects={() => setCurrentPage("projects")}
                  onOpenFeedback={() => setShowFeedbackModal(true)}
                />
              )}
              {currentPage === "clients" && (
                <ClientsPage
                  workspaceType={currentWorkspace}
                  onWorkspaceChange={setCurrentWorkspace}
                  initialSelectedClientId={
                    selectedClientId as Id<"clients"> | null
                  }
                  onClientDeselected={() => setSelectedClientId(null)}
                  onViewProjects={(clientId) => {
                    setClientFilter(clientId);
                    setSelectedClientId(null);
                    setCurrentPage("projects");
                  }}
                />
              )}
              {currentPage === "projects" &&
                (selectedProjectId ? (
                  <ProjectDetailPage
                    projectId={selectedProjectId}
                    onBackToProjects={() => {
                      setSelectedProjectId(null);
                      setProjectDetailTab("tasks");
                    }}
                    onNavigateToTimer={() => {
                      setCurrentPage("timer");
                    }}
                    onEntryLimitReached={() => {
                      setPaywallMode("limit");
                      setShowGlobalPaywall(true);
                    }}
                    initialTab={projectDetailTab}
                  />
                ) : (
                  <ProjectsPage
                    workspaceType={currentWorkspace}
                    onWorkspaceChange={setCurrentWorkspace}
                    clientFilter={clientFilter}
                    onClearClientFilter={() => setClientFilter(null)}
                    onProjectSelect={(projectId: string) => {
                      setSelectedProjectId(projectId);
                      setProjectDetailTab("tasks");
                    }}
                    onStartTimer={async (projectId: string) => {
                      try {
                        await startTimerMutation({
                          projectId: projectId as Id<"projects">,
                          pomodoroEnabled: false,
                        });
                        setCurrentPage("timer");
                        toast.success("Timer started! Switched to timer.");
                      } catch (error) {
                        toast.error("Failed to start timer");
                      }
                    }}
                    onOpenRetainerDashboard={() =>
                      setCurrentPage("systemDashboard")
                    }
                  />
                ))}
              {currentPage === "settings" && (
                <Settings
                  onNavigate={handleNavigate}
                  initialTab={settingsTab}
                />
              )}
              {currentPage === "profile" && (
                <ProfilePage
                  user={loggedInUser}
                  onNavigate={handleNavigate}
                  onShowPaywall={() => {
                    setPaywallMode("upgrade");
                    setShowGlobalPaywall(true);
                  }}
                />
              )}
              {currentPage === "testEmail" && (
                <TestEmailPage onNavigate={handleNavigate} />
              )}
              {currentPage === "systemDashboard" && <SystemDashboardPage />}
              {currentPage === "feedbackAdmin" && <FeedbackAdmin />}
            </Suspense>
          </div>
        </main>

        {/* Mobile Bottom Navigation */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 shadow-lg">
          <div className="flex items-center justify-around py-2">
            <BottomNavButton
              icon={<TimerIcon />}
              label="Timer"
              isActive={currentPage === "timer"}
              onClick={() => setCurrentPage("timer")}
            />
            <BottomNavButton
              icon={<DashboardIcon />}
              label="Dashboard"
              isActive={currentPage === "systemDashboard"}
              onClick={() => setCurrentPage("systemDashboard")}
            />
            <BottomNavButton
              icon={<ClientsIcon />}
              label="Clients"
              isActive={currentPage === "clients"}
              onClick={() => setCurrentPage("clients")}
            />
            <BottomNavButton
              icon={<ProjectsIcon />}
              label="Projects"
              isActive={currentPage === "projects"}
              onClick={() => setCurrentPage("projects")}
            />
          </div>
        </nav>
      </div>

      {/* Global Paywall Modal for tasks/projects pages */}
      <GlobalPaywallModal
        open={showGlobalPaywall}
        onOpenChange={setShowGlobalPaywall}
        mode={paywallMode}
      />

      {/* Feedback Modal */}
      <FeedbackModal
        open={showFeedbackModal}
        onOpenChange={setShowFeedbackModal}
      />
    </OrganizationProvider>
  );
}

function GlobalPaywallModal({
  open,
  onOpenChange,
  mode,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "limit" | "upgrade";
}) {
  const { activeOrganization } = useOrganization();
  const entryLimitStatus = useQuery(
    api.subscriptions.checkEntryLimit,
    activeOrganization?._id
      ? { organizationId: activeOrganization._id }
      : "skip",
  );
  const subscriptionStatus = useQuery(api.subscriptions.getSubscriptionStatus);
  const teamInviteLimit = useQuery(
    api.subscriptions.checkTeamInviteLimit,
    activeOrganization?._id
      ? { organizationId: activeOrganization._id }
      : "skip",
  );

  return (
    <Suspense fallback={null}>
      <PaywallModal
        open={open}
        onOpenChange={onOpenChange}
        currentCount={entryLimitStatus?.currentCount ?? 0}
        limit={entryLimitStatus?.limit ?? 100}
        workspaceName={activeOrganization?.name}
        onPurchaseSuccess={() => onOpenChange(false)}
        mode={mode}
        currentPlan={subscriptionStatus?.plan ?? "free"}
        currentMemberCount={teamInviteLimit?.currentMembers ?? 0}
      />
    </Suspense>
  );
}

function UnauthenticatedView() {
  const { page, navigate } = useMarketingPage();
  const [isMobile, setIsMobile] = useState(isMobileDevice());
  const [mobileAuthPage, setMobileAuthPage] =
    useState<MobileAuthPage>("landing");
  const [desktopAuthPage, setDesktopAuthPage] =
    useState<MobileAuthPage>("landing");
  const { signIn } = useAuthActions();

  // Update mobile detection on resize
  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle guest mode sign-in
  const handleGuestMode = async () => {
    try {
      await signIn("anonymous");
    } catch (error) {
      console.error("Guest sign-in failed:", error);
      toast.error("Failed to start guest session. Please try again.");
    }
  };

  // If mobile, show mobile-specific pages
  if (isMobile) {
    return (
      <Suspense fallback={<PageLoader />}>
        {mobileAuthPage === "sign-in" && (
          <MobileSignIn
            onBack={() => setMobileAuthPage("landing")}
            onSignUpLink={() => setMobileAuthPage("sign-up")}
          />
        )}
        {mobileAuthPage === "sign-up" && (
          <MobileSignUp
            onBack={() => setMobileAuthPage("landing")}
            onSignInLink={() => setMobileAuthPage("sign-in")}
          />
        )}
        {mobileAuthPage === "landing" && (
          <MobileLandingPage
            onSignIn={() => setMobileAuthPage("sign-in")}
            onSignUp={() => setMobileAuthPage("sign-up")}
            onGuestMode={handleGuestMode}
          />
        )}
      </Suspense>
    );
  }

  // Desktop view - new auth page routing
  // Show desktop auth pages when on home (not on marketing pages like /features)
  if (page === "home") {
    return (
      <Suspense fallback={<PageLoader />}>
        {desktopAuthPage === "sign-in" && (
          <DesktopSignIn
            onBack={() => setDesktopAuthPage("landing")}
            onSignUpLink={() => setDesktopAuthPage("sign-up")}
          />
        )}
        {desktopAuthPage === "sign-up" && (
          <DesktopSignUp
            onBack={() => setDesktopAuthPage("landing")}
            onSignInLink={() => setDesktopAuthPage("sign-in")}
          />
        )}
        {desktopAuthPage === "landing" && (
          <DesktopLandingPage
            onSignIn={() => setDesktopAuthPage("sign-in")}
            onSignUp={() => setDesktopAuthPage("sign-up")}
            onGuestMode={handleGuestMode}
            onNavigate={navigate}
          />
        )}
      </Suspense>
    );
  }

  // Desktop marketing pages (features, pricing, etc.)
  return (
    <Suspense fallback={<PageLoader />}>
      {page === "features" && <FeaturesPage onNavigate={navigate} />}
      {page === "pricing" && <PricingPage onNavigate={navigate} />}
      {page === "faq" && <FaqPage onNavigate={navigate} />}
      {page === "about" && <AboutPage onNavigate={navigate} />}
      {page === "privacy" && <PrivacyPolicyPage onNavigate={navigate} />}
      {page === "support" && <SupportPage onNavigate={navigate} />}
      {page === "terms" && <TermsPage onNavigate={navigate} />}
    </Suspense>
  );
}

function LandingPage({
  onNavigate,
}: {
  onNavigate: (page: MarketingPageSlug) => void;
}) {
  return (
    <div className="min-h-screen landing-gradient flex flex-col">
      <header className="px-6 pt-8">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <img src="/icon.png" alt="iTimedIT" className="h-12 w-12" />
            <div>
              <p className="text-2xl font-semibold text-slate-800">iTimedIT</p>
              <p className="text-sm text-slate-500">
                Professional time tracking made simple
              </p>
            </div>
          </div>
          <nav className="flex flex-wrap items-center gap-5 text-sm font-medium text-slate-600">
            <LandingNavLink page="features" onNavigate={onNavigate}>
              Features
            </LandingNavLink>
            <LandingNavLink page="pricing" onNavigate={onNavigate}>
              Pricing
            </LandingNavLink>
            <LandingNavLink page="faq" onNavigate={onNavigate}>
              FAQ
            </LandingNavLink>
            <LandingNavLink page="support" onNavigate={onNavigate}>
              Support
            </LandingNavLink>
            <LandingNavLink page="about" onNavigate={onNavigate}>
              About
            </LandingNavLink>
          </nav>
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-6 pb-6">
        <div className="w-full max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Hero Content */}
            <div className="text-center lg:text-left space-y-8">
              <div className="space-y-6">
                <div className="flex items-center justify-center lg:justify-start gap-3 mb-6">
                  <img src="/icon.png" alt="iTimedIT" className="h-12 w-12" />
                  <h1 className="text-4xl lg:text-5xl font-bold hero-text">
                    iTimedIT
                  </h1>
                </div>

                <h2 className="text-2xl lg:text-3xl font-semibold hero-text leading-tight">
                  Professional Time Tracking
                  <br />
                  <span className="hero-subtitle">Made Simple</span>
                </h2>

                <p className="text-lg hero-subtitle max-w-lg leading-relaxed">
                  Track time efficiently, manage project budgets, and stay
                  focused on what matters most to your business.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <div className="flex items-center gap-3 text-slate-600">
                  <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                  <span className="text-sm font-medium">
                    Real-time tracking
                  </span>
                </div>
                <div className="flex items-center gap-3 text-slate-600">
                  <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                  <span className="text-sm font-medium">Budget management</span>
                </div>
                <div className="flex items-center gap-3 text-slate-600">
                  <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                  <span className="text-sm font-medium">
                    Team collaboration
                  </span>
                </div>
              </div>
            </div>

            {/* Right side - Auth Form */}
            <div className="flex justify-center">
              <div className="w-full max-w-md">
                <div className="auth-card">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-semibold hero-text mb-2">
                      Get Started
                    </h3>
                    <p className="hero-subtitle">
                      Create an account to get started
                    </p>
                  </div>
                  <SignInForm defaultFlow="signUp" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <footer className="px-6 pb-12">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-4 text-sm text-slate-500">
          <div className="flex flex-wrap items-center justify-center gap-6">
            <LandingNavLink
              page="features"
              onNavigate={onNavigate}
              className="hover:text-slate-700"
            >
              Features
            </LandingNavLink>
            <LandingNavLink
              page="pricing"
              onNavigate={onNavigate}
              className="hover:text-slate-700"
            >
              Pricing
            </LandingNavLink>
            <LandingNavLink
              page="faq"
              onNavigate={onNavigate}
              className="hover:text-slate-700"
            >
              FAQ
            </LandingNavLink>
            <LandingNavLink
              page="support"
              onNavigate={onNavigate}
              className="hover:text-slate-700"
            >
              Support
            </LandingNavLink>
            <LandingNavLink
              page="privacy"
              onNavigate={onNavigate}
              className="hover:text-slate-700"
            >
              Privacy
            </LandingNavLink>
            <LandingNavLink
              page="terms"
              onNavigate={onNavigate}
              className="hover:text-slate-700"
            >
              Terms
            </LandingNavLink>
            <LandingNavLink
              page="about"
              onNavigate={onNavigate}
              className="hover:text-slate-700"
            >
              About
            </LandingNavLink>
          </div>
          <p className="text-center">
            Trusted by professionals worldwide • Secure • Reliable • Efficient
          </p>
        </div>
      </footer>
    </div>
  );
}

function LandingNavLink({
  page,
  onNavigate,
  className,
  children,
}: {
  page: MarketingPageSlug;
  onNavigate: (page: MarketingPageSlug) => void;
  className?: string;
  children: React.ReactNode;
}) {
  const target = buildAppPath(marketingPageToPath(page));
  return (
    <a
      href={target}
      onClick={(event) => {
        event.preventDefault();
        onNavigate(page);
      }}
      className={`transition-colors hover:text-slate-900 ${className ?? ""}`}
    >
      {children}
    </a>
  );
}

function useMarketingPage() {
  const [page, setPage] = useState<MarketingPageSlug>(() =>
    getMarketingPageFromLocation(),
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const handler = () => {
      setPage(getMarketingPageFromLocation());
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  const navigate = useCallback((target: MarketingPageSlug) => {
    if (typeof window === "undefined") {
      setPage(target);
      return;
    }
    const targetPath = buildAppPath(marketingPageToPath(target));
    window.history.pushState({}, "", targetPath);
    setPage(target);
  }, []);

  return { page, navigate };
}

function getMarketingPageFromLocation(): MarketingPageSlug {
  if (typeof window === "undefined") {
    return "home";
  }

  const { pathname } = window.location;
  const relativePath = stripBasePath(pathname);

  if (relativePath === "/features" || relativePath.startsWith("/features/")) {
    return "features";
  }

  if (relativePath === "/pricing" || relativePath.startsWith("/pricing/")) {
    return "pricing";
  }

  if (relativePath === "/faq" || relativePath.startsWith("/faq/")) {
    return "faq";
  }

  if (relativePath === "/about" || relativePath.startsWith("/about/")) {
    return "about";
  }

  if (relativePath === "/privacy" || relativePath.startsWith("/privacy/")) {
    return "privacy";
  }

  if (relativePath === "/support" || relativePath.startsWith("/support/")) {
    return "support";
  }

  if (relativePath === "/terms" || relativePath.startsWith("/terms/")) {
    return "terms";
  }

  return "home";
}

function NavButton({
  icon,
  label,
  isActive,
  onClick,
}: {
  icon?: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded ${
        isActive
          ? "bg-[#F85E00]/10 text-[#F85E00] dark:bg-[#F85E00] dark:text-white"
          : "text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
      }`}
    >
      {icon && <div className="w-4 h-4">{icon}</div>}
      {label}
    </button>
  );
}

function BottomNavButton({
  icon,
  label,
  isActive,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center justify-center p-2 min-w-0 flex-1 ${
        isActive
          ? "text-[#F85E00]"
          : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
      }`}
    >
      <div className="w-6 h-6 mb-1">{icon}</div>
      <span className="text-xs font-medium truncate">{label}</span>
    </button>
  );
}

function InviteRoute({
  token,
  onComplete,
}: {
  token: string;
  onComplete: () => void;
}) {
  return (
    <Suspense fallback={<PageLoader />}>
      <InvitePage token={token} onComplete={onComplete} />
    </Suspense>
  );
}

function useInviteToken(): [string | null, () => void] {
  const [token, setToken] = useState<string | null>(() =>
    getInviteTokenFromLocation(),
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const handler = () => setToken(getInviteTokenFromLocation());
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  const clearToken = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    const targetPath = buildAppPath("/");
    window.history.replaceState({}, "", targetPath);
    setToken(null);
  }, []);

  return [token, clearToken];
}

function getInviteTokenFromLocation(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const { pathname, search } = window.location;
  const relativePath = stripBasePath(pathname);

  if (relativePath.startsWith("/invite/")) {
    const parts = relativePath.split("/").filter(Boolean);
    const inferred = parts[1] ?? null;
    if (inferred) {
      return inferred;
    }
  }

  const params = new URLSearchParams(search);
  return params.get("token");
}

// Icon components
function TimerIcon() {
  return (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      className="w-full h-full"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function ClientsIcon() {
  return (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      className="w-full h-full"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );
}

function ProjectsIcon() {
  return (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      className="w-full h-full"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
      />
    </svg>
  );
}

function HelpIcon() {
  return (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      className="w-full h-full"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      className="w-full h-full"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function ProfileIcon() {
  return (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      className="w-full h-full"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  );
}

function DashboardIcon() {
  return (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      className="w-full h-full"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  );
}

// Syncs frontend currentWorkspace state with backend activeOrganization
function WorkspaceSyncEffect({
  currentWorkspace,
  setCurrentWorkspace,
}: {
  currentWorkspace: "personal" | "work";
  setCurrentWorkspace: (workspace: "personal" | "work") => void;
}) {
  const { activeOrganization, isReady } = useOrganization();

  useEffect(() => {
    if (!isReady || !activeOrganization) {
      return;
    }

    // Determine workspace type from activeOrganization (check both fields)
    const isPersonal =
      activeOrganization.workspaceType === "personal" ||
      activeOrganization.isPersonalWorkspace === true;

    const derivedWorkspace: "personal" | "work" = isPersonal
      ? "personal"
      : "work";

    // Only update if different to avoid unnecessary re-renders
    if (currentWorkspace !== derivedWorkspace) {
      setCurrentWorkspace(derivedWorkspace);
    }
  }, [activeOrganization, isReady, currentWorkspace, setCurrentWorkspace]);

  return null;
}

function WorkspaceIndicator({
  currentWorkspace,
  onWorkspaceChange,
  onManageWorkspaces,
}: {
  currentWorkspace: "personal" | "work";
  onWorkspaceChange: (workspace: "personal" | "work") => void;
  onManageWorkspaces?: () => void;
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { memberships, activeOrganization, switchOrganization, isReady } =
    useOrganization();

  // Filter memberships into personal and work (check both fields for compatibility)
  const personalMembership = memberships.find(
    (item) =>
      item.organization?.workspaceType === "personal" ||
      item.organization?.isPersonalWorkspace === true,
  );
  const workMemberships = memberships.filter(
    (item) =>
      item.organization?.workspaceType !== "personal" &&
      item.organization?.isPersonalWorkspace !== true,
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const handleWorkspaceSwitch = async (
    organizationId: Id<"organizations">,
    isPersonal: boolean,
  ) => {
    if (!isReady) return;

    await switchOrganization(organizationId);
    onWorkspaceChange(isPersonal ? "personal" : "work");
    setShowDropdown(false);
  };

  // Display name and color for the button - use ACTIVE organization for accurate display
  const displayOrg =
    currentWorkspace === "personal"
      ? activeOrganization // Use actual active org, not first personal found (shows "Kieran" not "Personal")
      : workMemberships.find(
          (m) => m.organization?._id === activeOrganization?._id,
        )?.organization || workMemberships[0]?.organization;

  const displayName =
    displayOrg?.name || (currentWorkspace === "personal" ? "Personal" : "Work");
  const displayColor =
    currentWorkspace === "personal"
      ? "#3b82f6" // Blue for personal
      : displayOrg?.color || "#8b5cf6"; // Purple default for work

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <div
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: displayColor }}
        />
        <span>{displayName}</span>
        <svg
          className={`w-4 h-4 transition-transform ${showDropdown ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {showDropdown && (
        <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 overflow-hidden">
          {/* Personal Workspace */}
          {personalMembership?.organization && (
            <button
              onClick={() =>
                handleWorkspaceSwitch(
                  personalMembership.organization!._id,
                  true,
                )
              }
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                currentWorkspace === "personal"
                  ? "text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/20"
                  : "text-gray-700 dark:text-gray-200"
              }`}
            >
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: "#3b82f6" }}
              />
              <span>Personal</span>
              {currentWorkspace === "personal" && (
                <svg
                  className="w-4 h-4 ml-auto"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          )}

          {/* Divider if there are work memberships */}
          {workMemberships.length > 0 && personalMembership && (
            <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
          )}

          {/* Work Organizations */}
          {workMemberships.map(({ membership, organization }) => {
            const isActive =
              currentWorkspace === "work" &&
              activeOrganization?._id === organization?._id;
            const orgColor = organization?.color || "#8b5cf6";
            return (
              <button
                key={membership._id}
                onClick={() =>
                  organization && handleWorkspaceSwitch(organization._id, false)
                }
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                  isActive
                    ? "font-medium bg-gray-100 dark:bg-gray-700/50"
                    : "text-gray-700 dark:text-gray-200"
                }`}
              >
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: orgColor }}
                />
                <span className="truncate">
                  {organization?.name || "Workspace"}
                </span>
                {isActive && (
                  <svg
                    className="w-4 h-4 ml-auto flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            );
          })}

          {/* Manage Workspaces Option */}
          {onManageWorkspaces && (
            <>
              <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
              <button
                onClick={() => {
                  onManageWorkspaces();
                  setShowDropdown(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span className="font-medium">Manage Workspaces</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
